import React, { useEffect, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Radio,
  RadioGroup,
  Button,
  Textarea,
  VStack,
  Input,
  HStack,
  IconButton,
  FormLabel,
  Text,
  Divider,
  Flex,
  Box,
  Card,
  CardBody,
  Icon
} from '@chakra-ui/react';
import { AddIcon, CloseIcon, InfoOutlineIcon } from '@chakra-ui/icons';

const MAX_FAQS = 5;

const MessageTemplateModal = ({ isOpen, onClose, accountId, welcomeData, onSave }) => {
  const [greeting, setGreeting] = useState('');
  const [mode, setMode] = useState('autofill'); // 'autofill' or 'faq'
  const [autofillText, setAutofillText] = useState('');
  const [faqs, setFaqs] = useState([{ title: '', response: '' }]);

  useEffect(() => {
    const type = welcomeData?.text_format?.customer_action_type;
    setMode(type === 'ice_breakers' ? 'faq' : 'autofill');
    setGreeting(welcomeData?.text_format?.message?.text || 'Hi! Please let us know how we can help you.');
    setAutofillText(welcomeData?.text_format?.message?.autofill_message?.content || 'Hello! Can I get more info on this?');
    setFaqs(
      welcomeData?.text_format?.message?.ice_breakers?.length > 0
        ? welcomeData.text_format.message.ice_breakers.map((faq) => ({ ...faq }))
        : [{ title: `Hi! 👋 I'd like to know more. Is anyone free to chat?`, response: '' }]
    );    
  }, [welcomeData]);

  const handleAddFAQ = () => {
    if (faqs.length < MAX_FAQS) {
      setFaqs([...faqs, { title: '', response: '' }]);
    }
  };

  const handleRemoveFAQ = (index) => {
    const updated = [...faqs];
    updated.splice(index, 1);
    setFaqs(updated);
  };

  const handleChangeFAQ = (index, field, value) => {
    console.log("🚀 ~ handleChangeFAQ ~ field:", field)
    console.log("🚀 ~ handleChangeFAQ ~ value:", value)
    console.log("🚀 ~ handleChangeFAQ ~ index:", index)
    const updated = [...faqs];
    updated[index][field] = value;
    setFaqs(updated);
  };

  const handleSave = () => {
    const base = {
      ...(accountId && { adsAccountId: accountId }),
      type: 'VISUAL_EDITOR',
      version: 2,
      landing_screen_type: 'welcome_message',
      media_type: 'text',
      surface: 'visual_editor_new',
      user_edit: false,
    };

    const updated =
      mode === 'autofill'
        ? {
            ...base,
            text_format: {
              customer_action_type: 'autofill_message',
              message: {
                text: greeting,
                autofill_message: {
                  content: autofillText,
                },
              },
            },
          }
        : {
            ...base,
            text_format: {
              customer_action_type: 'ice_breakers',
              message: {
                text: greeting,
                ice_breakers: faqs.filter((f) => f.title.trim()),
              },
            },
          };

    onSave(updated);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize={'sm'}  boxShadow="sm">Edit Template</ModalHeader>
        <ModalCloseButton />
        <ModalBody maxH="70vh" overflowY="auto" pr={4}>
            <Flex direction={'column'} mb={2}>
                <Text fontWeight={'bold'}>Greeting</Text>
                <Text fontSize={'12px'} mb={2}>Welcome people to the conversation after they tap on your ad.</Text>
                <Text fontWeight={'bold'} fontSize={'14px'}>Text</Text>
                <Textarea
                    value={greeting}
                    onChange={(e) => setGreeting(e.target.value)}
                    placeholder="Hi! Please let us know how we can help you."
                    rows={2}
                    size={'sm'}
                    borderRadius={'md'}
                />
            </Flex>
            
            <Divider my={2}/>
            <Flex direction={'column'} gap={1} mb={2}>
                <Text fontWeight={'bold'}>Customer actions</Text>
                <Text fontSize={'12px'}>Suggest questions or messages that people can send.</Text>
            </Flex>

            <RadioGroup value={mode} onChange={(val) => setMode(val)} mb={4}>
            <HStack spacing={4} direction="column" align="start" width="100%">
                <Flex direction="column" gap={2} width="100%">
                
                {/* Autofill Option */}
                <Flex direction="column" width="100%">
                    <Radio value="autofill" colorScheme="brand">
                        <Text fontWeight="bold">Pre-filled Message</Text>
                    </Radio>

                    <Text fontSize="12px" ml={6}>Create a customizable first message.</Text>
                    {mode === 'autofill' && (
                    <Flex pl={6} pt={2} width="100%" direction="column" gap={2}>
                        <Flex alignItems={'center'} gap={2}>
                            <Input
                                value={autofillText}
                                onChange={(e) => setAutofillText(e.target.value.slice(0, 80))}
                                size={'sm'}
                                borderRadius={'md'}
                            />
                            <Text fontSize="sm" color="gray.500">
                                {autofillText?.length}/80
                            </Text>
                        </Flex>
                        <Card
                            borderRadius="5px"
                            p={2}
                            borderLeft={'4px solid gray'}
                        >
                            <CardBody display="flex" gap={2} p={0}>
                                <InfoOutlineIcon
                                    style={{ pointerEvents: 'all', marginLeft: '4px' }}
                                />
                                <Text fontSize="12px">
                                    Start with a friendly greeting followed by a message relevant to your ad content.
                                </Text>
                            </CardBody>
                        </Card>
                    </Flex>
                    )}
                </Flex>

                {/* FAQ Option */}
                <Flex direction="column" width="100%">
                    <Radio value="faq" colorScheme="brand">
                        <Text fontWeight="bold">Frequently asked questions</Text>
                    </Radio>

                    <Text fontSize="12px" ml={6}>Suggest questions for people to tap.</Text>
                    {mode === 'faq' && (
                    <Box pl={6} pt={2} width="100%">
                        <VStack spacing={3}>
                        {faqs?.map((faq, index) => (
                            <Box key={index} width="100%">
                            <Text fontWeight={'bold'} fontSize={'sm'}>Question #{index + 1}</Text>
                            <HStack>
                                <Input
                                    placeholder="Question title"
                                    value={faq.title}
                                    onChange={(e) => handleChangeFAQ(index, 'title', e.target.value)}
                                    maxLength={80}
                                    borderRadius={'md'}
                                    size={'sm'}
                                />
                               <IconButton
                                    icon={<CloseIcon />}
                                    isDisabled={faqs.length === 1}
                                    variant="solid"
                                    colorScheme="gray"
                                    size="sm"
                                    onClick={() => handleRemoveFAQ(index)}
                                    aria-label="Remove question"
                                />
                            </HStack>
                            <Textarea
                                mt={2}
                                size={'sm'}
                                rows={2}
                                borderRadius={'md'}
                                placeholder="Response message"
                                value={faq.response}
                                onChange={(e) => handleChangeFAQ(index, 'response', e.target.value)}
                            />
                            </Box>
                        ))}
                        {faqs.length < MAX_FAQS && (
                           <Flex
                            align="center"
                            border="1px dashed"
                            borderColor="gray.300"
                            p={2}
                            width={'100%'}
                            cursor="pointer"
                            _hover={{ bg: 'gray.50' }}
                            onClick={handleAddFAQ}
                         >
                           <Icon as={AddIcon} mr={2} boxSize={2} color={'gray'}/>
                           <Text color={'gray'} fontSize={'sm'}>Add a question</Text>
                         </Flex>
                        )}
                        </VStack>
                    </Box>
                    )}
                </Flex>

                </Flex>
            </HStack>
            </RadioGroup>

        </ModalBody>
        <ModalFooter justifyContent={'space-between'} my={-1} boxShadow="sm"> 
            <Button onClick={onClose} colorScheme={'gray'} size={'sm'} variant={'solid'}>
            Cancel
          </Button>
          <Button colorScheme="blue" size={'sm'} onClick={handleSave}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default MessageTemplateModal;
